/**
 * Shared animation presets for plugins, re-exported from the web app's
 * canonical `motion.ts`. Kept in sync manually — the web package owns the
 * source of truth but plugins cannot import from `web/` directly (web is not
 * a package). Duplicating the preset values here avoids that coupling.
 *
 * All presets are Lenis-safe: they use CSS opacity/transform on mount/unmount
 * or IntersectionObserver (whileInView). None sample the scroll position directly.
 *
 * Easing curves:
 *   decelerate [0, 0, 0.2, 1]  — elements entering (most UI transitions)
 *   standard   [0.4, 0, 0.2, 1] — general state transitions
 *   accelerate [0.4, 0, 1, 1]   — elements leaving
 */

const decelerate = [0, 0, 0.2, 1] as const;
const accelerate = [0.4, 0, 1, 1] as const;

/**
 * Fade-in for top-level page transitions. No y movement and no exit animation.
 */
export const pageFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { duration: 0.15, ease: decelerate },
} as const;

/**
 * Fade up on enter, fade up-out on exit. Use for content panels and detail
 * views that live inside a page — not for the page wrapper itself.
 */
export const fadeInUp = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -4 },
  transition: { duration: 0.22, ease: decelerate },
} as const;

/**
 * Motion container that staggers its children. Pair with `staggerItem`.
 */
export const staggerContainer = {
  initial: 'initial',
  animate: 'animate',
  variants: {
    initial: {},
    animate: { transition: { staggerChildren: 0.035 } },
  },
} as const;

/**
 * Individual item inside a `staggerContainer`.
 */
export const staggerItem = {
  variants: {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.14, ease: decelerate } },
  },
  exit: { opacity: 0, transition: { duration: 0.1, ease: accelerate } },
} as const;

/** Subtle press and hover effect for clickable cards and interactive elements. */
export const microPress = {
  whileHover: { scale: 1.01 },
  whileTap: { scale: 0.98 },
  transition: { type: 'spring', stiffness: 500, damping: 30 },
} as const;

/**
 * Lightweight fade+slide for chat/turn items. Gentler than `fadeInUp` so
 * chat streams don't jitter under load.
 */
export const chatItemFade = {
  initial: { opacity: 0, y: 6 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -2 },
  transition: { duration: 0.18, ease: decelerate },
} as const;
